import { format } from 'date-fns'
import { TOOLTIP } from '../../../../constants/tooltips'
import { FormFieldsEdit } from '../../../../types/form-types'
import { SetValue, ValidationErrors } from '../../../hooks/use-edit-form'
import { DISPLAY_DATE_FORMAT, INTERNAL_DATE_FORMAT } from '../../../services/constants'
import { parseDate } from '../../aoraki/generic-search-common/src'
import LookupField from '../../wells/lookup-field'
import { preventChangeOnScroll } from '../../../services/form-utils'
import { Card, DateField, FormField, FormFieldRow, Heading, InputWidth, TextInput } from '@msaf/core-react'

interface WellHeadConstructionEditInterface {
  fields: Partial<FormFieldsEdit>
  isLoading: boolean
  setValue: SetValue<FormFieldsEdit>
  validationErrors: ValidationErrors
}

export default function WellHeadConstructionEdit({
  fields = {},
  isLoading,
  setValue,
  validationErrors,
}: WellHeadConstructionEditInterface) {
  return (
    <>
      <FormField
        toolTipMessage={TOOLTIP.casingHeight}
        isSkeleton={isLoading}
        label='Ground level to top of casing (m)'
        htmlFor='casingHeight'
        validationMessages={validationErrors?.casingHeight}
      >
        <TextInput
          fieldSize={InputWidth.medium}
          isSkeleton={isLoading}
          type='number'
          onWheel={preventChangeOnScroll}
          id='casingHeight'
          value={`${fields.casingHeight ?? ''}`}
          setValue={(value) => setValue('casingHeight', value)}
          isInvalid={!!validationErrors.casingHeight}
        />
      </FormField>

      <FormField
        toolTipMessage={TOOLTIP.elevationRefPointDescription}
        isSkeleton={isLoading}
        label='Elevation reference point description'
        htmlFor='elevationRefPointDescription'
        validationMessages={validationErrors?.elevationRefPointDescription}
      >
        <TextInput
          isSkeleton={isLoading}
          id='elevationRefPointDescription'
          value={`${fields.elevationRefPointDescription ?? ''}`}
          setValue={(value) => setValue('elevationRefPointDescription', value)}
          isInvalid={!!validationErrors.elevationRefPointDescription}
        />
      </FormField>

      <FormField
        toolTipMessage={TOOLTIP.groundLevelCorrection}
        isSkeleton={isLoading}
        label='Ground level correction (m)'
        htmlFor='groundLevelCorrection'
        validationMessages={validationErrors?.groundLevelCorrection}
      >
        <TextInput
          fieldSize={InputWidth.medium}
          isSkeleton={isLoading}
          type='number'
          onWheel={preventChangeOnScroll}
          id='groundLevelCorrection'
          value={`${fields.groundLevelCorrection ?? ''}`}
          setValue={(value) => setValue('groundLevelCorrection', value)}
          isInvalid={!!validationErrors.groundLevelCorrection}
        />
      </FormField>

      <FormField
        validationMessages={validationErrors?.measuringPointDescription}
        toolTipMessage={TOOLTIP.measuringPointDescription}
        isSkeleton={isLoading}
        label='GWL measuring point description'
        htmlFor='measuringPointDescription'
      >
        <TextInput
          isSkeleton={isLoading}
          id='measuringPointDescription'
          value={`${fields.measuringPointDescription ?? ''}`}
          setValue={(value) => setValue('measuringPointDescription', value)}
          isInvalid={!!validationErrors.measuringPointDescription}
        />
      </FormField>

      <FormField
        validationMessages={validationErrors?.measuringPointCorrection}
        toolTipMessage={TOOLTIP.measuringPointCorrection}
        isSkeleton={isLoading}
        label='GWL measuring point correction (m)'
        htmlFor='measuringPointCorrection'
      >
        <TextInput
          fieldSize={InputWidth.medium}
          isSkeleton={isLoading}
          type='number'
          onWheel={preventChangeOnScroll}
          id='measuringPointCorrection'
          value={`${fields.measuringPointCorrection ?? ''}`}
          setValue={(value) => setValue('measuringPointCorrection', value)}
          isInvalid={!!validationErrors.measuringPointCorrection}
        />
      </FormField>

      <Card>
        <Heading isSkeleton={isLoading} level={4}>
          New Zealand Vertical Datum 2016
        </Heading>

        <FormFieldRow>
          <FormField
            validationMessages={validationErrors?.nzvdElevation}
            toolTipMessage={TOOLTIP.measuringPointElevationNzvd2016}
            isSkeleton={isLoading}
            label='Elevation'
            htmlFor='nzvdElevation'
          >
            <TextInput
              isSkeleton={isLoading}
              type='number'
              onWheel={preventChangeOnScroll}
              id='nzvdElevation'
              value={`${fields.nzvdElevation ?? ''}`}
              setValue={(value) => setValue('nzvdElevation', value)}
              isInvalid={!!validationErrors.nzvdElevation}
            />
          </FormField>

          <FormField
            validationMessages={validationErrors?.nzvdElevationAccuracy}
            isSkeleton={isLoading}
            label='Elevation accuracy'
            labelId='nzvdElevationAccuracyLabel'
          >
            <LookupField
              isSkeleton={isLoading}
              labelledBy='nzvdElevationAccuracyLabel'
              fieldId='nzvdElevationAccuracy'
              value={`${fields.nzvdElevationAccuracy ?? ''}`}
              setValue={({ value }) => setValue('nzvdElevationAccuracy', value)}
              isInvalid={!!validationErrors.nzvdElevationAccuracy}
            />
          </FormField>
        </FormFieldRow>

        <FormFieldRow>
          <FormField
            validationMessages={validationErrors?.nzvdMethod}
            toolTipMessage={TOOLTIP.measuringPointLocalMethodId}
            isSkeleton={isLoading}
            label='Method'
            labelId='nzvdMethodLabel'
          >
            <LookupField
              isSkeleton={isLoading}
              labelledBy='nzvdMethodLabel'
              fieldId='nzvdMethod'
              value={`${fields.nzvdMethod ?? ''}`}
              setValue={({ value }) => setValue('nzvdMethod', value)}
              isInvalid={!!validationErrors.nzvdMethod}
            />
          </FormField>

          <FormField
            validationMessages={validationErrors?.nzvdDate}
            toolTipMessage={TOOLTIP.measuringPointNzvdDate}
            isSkeleton={isLoading}
            label='Date'
            htmlFor='nzvdDate'
          >
            <DateField
              isInvalid={!!validationErrors.nzvdDate}
              isSkeleton={isLoading}
              isDisabled={isLoading}
              fieldSize={InputWidth.large}
              dateFormat={DISPLAY_DATE_FORMAT}
              selectedDate={parseDate(fields.nzvdDate)}
              setSelectedDate={(date: Date) => setValue('nzvdDate', date ? format(date, INTERNAL_DATE_FORMAT) : '')}
            />
          </FormField>
        </FormFieldRow>

        <FormField
          validationMessages={validationErrors?.measuringPointNzvdSurveyBy}
          toolTipMessage={TOOLTIP.measuringPointNzvdSurveyBy}
          isSkeleton={isLoading}
          label='Surveyed by'
          htmlFor='measuringPointNzvdSurveyBy'
        >
          <TextInput
            isInvalid={!!validationErrors.measuringPointNzvdSurveyBy}
            isSkeleton={isLoading}
            id='measuringPointNzvdSurveyBy'
            value={`${fields.measuringPointNzvdSurveyBy ?? ''}`}
            setValue={(value) => setValue('measuringPointNzvdSurveyBy', value)}
          />
        </FormField>
      </Card>

      <Card>
        <Heading isSkeleton={isLoading} level={4}>
          Local datum
        </Heading>
        <FormField
          validationMessages={validationErrors?.localDatum}
          isSkeleton={isLoading}
          label='Datum type'
          labelId='datumTypeLabel'
        >
          <LookupField
            isInvalid={!!validationErrors.localDatum}
            isSkeleton={isLoading}
            labelledBy='datumTypeLabel'
            fieldId='localDatum'
            fieldFilters={{ councilId: fields.council ?? '' }}
            value={`${fields.localDatum ?? ''}`}
            setValue={({ value }) => setValue('localDatum', value)}
          />
        </FormField>
        <FormFieldRow>
          <FormField
            validationMessages={validationErrors?.localElevation}
            toolTipMessage={TOOLTIP.measuringPointLocalElevation}
            isSkeleton={isLoading}
            label='Elevation'
            htmlFor='localElevation'
          >
            <TextInput
              isInvalid={!!validationErrors.localElevation}
              isSkeleton={isLoading}
              type='number'
              onWheel={preventChangeOnScroll}
              id='localElevation'
              value={`${fields.localElevation ?? ''}`}
              setValue={(value) => setValue('localElevation', value)}
            />
          </FormField>

          <FormField
            validationMessages={validationErrors?.localElevationAccuracy}
            isSkeleton={isLoading}
            label='Elevation accuracy'
            labelId='localElevationAccuracyLabel'
          >
            <LookupField
              isInvalid={!!validationErrors.localElevationAccuracy}
              isSkeleton={isLoading}
              labelledBy='localElevationAccuracyLabel'
              fieldId='localElevationAccuracy'
              value={`${fields.localElevationAccuracy ?? ''}`}
              setValue={({ value }) => setValue('localElevationAccuracy', value)}
            />
          </FormField>
        </FormFieldRow>

        <FormFieldRow>
          <FormField
            validationMessages={validationErrors?.localMethod}
            toolTipMessage={TOOLTIP.measuringPointLocalMethodId}
            isSkeleton={isLoading}
            label='Method'
            labelId='localMethodLabel'
          >
            <LookupField
              isInvalid={!!validationErrors.localMethod}
              isSkeleton={isLoading}
              labelledBy='localMethodLabel'
              fieldId='localMethod'
              value={`${fields.localMethod ?? ''}`}
              setValue={({ value }) => setValue('localMethod', value)}
            />
          </FormField>

          <FormField
            validationMessages={validationErrors?.localDate}
            toolTipMessage={TOOLTIP.measuringPointLocalDate}
            isSkeleton={isLoading}
            label='Date'
            htmlFor='localDate'
          >
            <DateField
              isInvalid={!!validationErrors.localDate}
              isSkeleton={isLoading}
              isDisabled={isLoading}
              fieldSize={InputWidth.large}
              dateFormat={DISPLAY_DATE_FORMAT}
              selectedDate={parseDate(fields.localDate)}
              setSelectedDate={(date: Date) => setValue('localDate', date ? format(date, INTERNAL_DATE_FORMAT) : '')}
            />
          </FormField>
        </FormFieldRow>

        <FormField
          validationMessages={validationErrors?.measuringPointLocalSurveyBy}
          toolTipMessage={TOOLTIP.measuringPointLocalSurveyBy}
          isSkeleton={isLoading}
          label='Surveyed by'
          htmlFor='measuringPointLocalSurveyBy'
        >
          <TextInput
            isInvalid={!!validationErrors.measuringPointLocalSurveyBy}
            isSkeleton={isLoading}
            id='measuringPointLocalSurveyBy'
            value={`${fields.measuringPointLocalSurveyBy ?? ''}`}
            setValue={(value) => setValue('measuringPointLocalSurveyBy', value)}
          />
        </FormField>
      </Card>
    </>
  )
}
